/**
 * frontend/src/services/api.js
 * 
 * API service layer using Axios for making HTTP requests to the backend.
 * It handles base URL configuration, request/reponse interceptors for authentication,
 * and organizes API calls into logical groups.
 * 
 * Common API groups:
 * - authAPI: Handles user registration, login (email/Google), and fetching current user info.
 * - workspacesAPI: Manages workspace lifecycle, membership, and activity logs.
 * - changeRequestsAPI: Handles CRUD operations and comments for compliance change requests.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect for auth endpoints (login, register, etc.) to allow
    // the components to handle the error (display error message instead of reloading)
    const isAuthEndpoint = error.config?.url?.includes('/auth/');

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => {
    const formData = new URLSearchParams();
    formData.append('username', data.email);
    formData.append('password', data.password);
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  googleCodeExchange: (code, redirectUri) => api.post('/auth/google/code', { code, redirect_uri: redirectUri }),
  getMe: () => api.get('/auth/me'),
  getInviteInfo: (token) => api.get(`/auth/invite-info?token=${encodeURIComponent(token)}`),
  acceptInvite: (data) => api.post('/auth/accept-invite', data),
};

// Workspaces API
export const workspacesAPI = {
  getAll: () => api.get('/workspaces'),
  create: (data) => api.post('/workspaces', data),
  get: (id) => api.get(`/workspaces/${id}`),
  update: (id, data) => api.put(`/workspaces/${id}`, data),
  join: (inviteCode) => api.post('/workspaces/join', { invite_code: inviteCode }),
  regenerateInvite: (id) => api.post(`/workspaces/${id}/regenerate-invite`),
  getMembers: (id) => api.get(`/workspaces/${id}/members`),
  updateMember: (workspaceId, userId, data) =>
    api.put(`/workspaces/${workspaceId}/members/${userId}`, data),
  removeMember: (workspaceId, userId) =>
    api.delete(`/workspaces/${workspaceId}/members/${userId}`),
  getActivity: (id, limit = 50) => api.get(`/workspaces/${id}/activity?limit=${limit}`),
  inviteMembers: (workspaceId, emails) =>
    api.post(`/workspaces/${workspaceId}/invite`, { emails }),
  getInvites: (workspaceId) => api.get(`/workspaces/${workspaceId}/invites`),
  cancelInvite: (workspaceId, inviteId) =>
    api.delete(`/workspaces/${workspaceId}/invites/${inviteId}`),
  getAuditLogs: (workspaceId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.resource_type) queryParams.append('resource_type', params.resource_type);
    if (params.action) queryParams.append('action', params.action);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);

    return api.get(`/workspaces/${workspaceId}/audit-logs?${queryParams.toString()}`);
  },
};

// Change Requests API
export const changeRequestsAPI = {
  getAll: (workspaceId) => api.get(`/workspaces/${workspaceId}/changes`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/changes`, data),
  get: (id) => api.get(`/changes/${id}`),
  update: (id, data) => api.put(`/changes/${id}`, data),
  getLogs: (id) => api.get(`/changes/${id}/logs`),
  addComment: (id, comment) => api.post(`/changes/${id}/comment`, { comment }),
};

// Templates API
export const templatesAPI = {
  getAll: (workspaceId) => api.get(`/workspaces/${workspaceId}/templates`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/templates`, data),
  get: (id) => api.get(`/templates/${id}`),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Subscription API
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  createCheckout: (returnUrl) => api.post('/subscription/checkout', { return_url: returnUrl }),
  getLimits: () => api.get('/subscription/limits'),
};

export default api;
